<template>
    <NuxtLayout>
        <NuxtPage />
        <Toaster />
    </NuxtLayout>
</template>
<script setup lang="ts">
import type { ResolvableLink, Script } from "@unhead/vue";

const { t, locale } = useI18n();
const i18nHead = useLocaleHead({ seo: true });
const config = useRuntimeConfig();
const route = useRoute();
const initializeState = useInitializeState();
const { gtag } = useGtag();
const cookieConsent = useCookieConsent();

const { cdnUrl, enableDatadome } = config.public;

const preconnectLinks: ResolvableLink[] = [];
const serverScripts: Script[] = [];

if (enableDatadome) {
    const dataDomeJs = `window.ddjskey = '7012F816DF6737C9087618574AE3B2'; window.ddoptions = { endpoint: "https://dd.trustedparts.com/js/", ajaxListenerPath: true };`;
    serverScripts.push(
        { innerHTML: dataDomeJs, tagPriority: 0 },
        { src: "https://dd.trustedparts.com/tags.js", async: true, tagPriority: "high" }
    );
    preconnectLinks.push({ rel: "preconnect", href: "https://dd.trustedparts.com" });
}

useHead({
    titleTemplate: (title) => {
        if (!title) title = t("Global.TitleDefault");
        return title.includes("TrustedParts.com") ? title : title + " " + t("Global.TitleSuffix");
    },
});

await initializeState();

if (import.meta.server) {
    useHead({
        htmlAttrs: {
            lang: locale.value,
        },
        link: [
            { rel: "apple-touch-icon", sizes: "180x180", href: cdnUrl + "/apple-touch-icon.png" },
            { rel: "manifest", href: cdnUrl + "/site.webmanifest" },
            { rel: "mask-icon", href: cdnUrl + "/safari-pinned-tab.svg", color: "#00274e" },
            ...(i18nHead.value.link || []).filter((link) => link.hid !== "i18n-xd"), // remove default language alternate link
            ...preconnectLinks,
        ],
        meta: [
            { name: "msapplication-TileColor", content: "#00274e" },
            { name: "theme-color", content: "#00274e" },
            ...(i18nHead.value.meta || []),
        ],
        script: serverScripts,
    });
    // Global SEO tags common to all pages.
    useServerSeoMeta({
        ogType: "website",
        ogSiteName: "TrustedParts.com",
        ogImage: cdnUrl + "/assets/images/trusted-parts-logo.svg",
    });
} else {
    const userInfo = useStateUserInfo();

    if (userInfo.value.IsCookieConsentNeeded || route.query["load-cookie-consent"]) {
        cookieConsent.init();
    }
    if (!userInfo.value.AcceptsAnalyticsCookies) {
        gtag("consent", "default", { ad_storage: "denied", analytics_storage: "denied" });
    }
}
</script>
